/** Learning Path completion utilities — single source of truth across components. */

import type { UserLearningPath } from '@/types/learning-paths';

/**
 * True when every course in user LP progress is completed or at 100%.
 * Empty or missing courses → false (do not treat as "all done").
 */
export function areAllUserLpCoursesCompleted(
  courses: UserLearningPath['courses'] | undefined
): boolean {
  if (!courses || courses.length === 0) return false;
  return courses.every(
    (c) => c.progress?.isCompleted === true || (c.progress?.percentage ?? 0) >= 100
  );
}

/**
 * LP final assessment unlock (subscription enforced at call sites).
 * `lpIsLearningMaterialCompleted` must be honored on its own: the list endpoint
 * (`/v1/users/learning-paths`) returns no `courses[]`, so `allCoursesCompleted`
 * is always false there — without it the dashboard would diverge from the LP
 * detail page, which computes it from per-course data (see DIG-11311).
 */
export function canUnlockLearningPathAssessment(args: {
  allCoursesCompleted: boolean;
  lpProgressPercent?: number;
  lpIsCompleted?: boolean;
  lpIsLearningMaterialCompleted?: boolean;
}): boolean {
  const pct = args.lpProgressPercent ?? 0;
  return (
    args.allCoursesCompleted ||
    args.lpIsLearningMaterialCompleted === true ||
    pct >= 100 ||
    args.lpIsCompleted === true
  );
}

/** Unlock from user LP payload (courses[] from API). */
export function computeLpAssessmentUnlocked(
  userLp: Pick<UserLearningPath, 'progress' | 'courses'> | null | undefined
): boolean {
  if (!userLp) return false;
  return canUnlockLearningPathAssessment({
    allCoursesCompleted: areAllUserLpCoursesCompleted(userLp.courses),
    lpProgressPercent: userLp.progress?.percentage,
    lpIsCompleted: userLp.progress?.isCompleted,
    lpIsLearningMaterialCompleted: userLp.progress?.isLearningMaterialCompleted,
  });
}

/** Unlock when CMS+progress merged courses are available (hero, LP page, assessment route). */
export function computeLpAssessmentUnlockedFromMergedCourses(
  userLp: Pick<UserLearningPath, 'progress'> | null | undefined,
  courses: Array<{ isCompleted?: boolean; userProgress?: { percentage?: number } }>
): boolean {
  const allCoursesCompleted =
    courses.length > 0 &&
    courses.every(
      (c) => c.isCompleted === true || (c.userProgress?.percentage ?? 0) >= 100
    );
  return canUnlockLearningPathAssessment({
    allCoursesCompleted,
    lpProgressPercent: userLp?.progress?.percentage,
    lpIsCompleted: userLp?.progress?.isCompleted,
    lpIsLearningMaterialCompleted: userLp?.progress?.isLearningMaterialCompleted,
  });
}

/** LP completion: trusts API's `isCompleted` when set, else falls back to `progressPercent >= 100`. */
export function isLearningPathCompleted(
  isCompleted?: boolean,
  progressPercent?: number
): boolean {
  if (isCompleted === true) return true;
  if (isCompleted === false) return false;
  return (progressPercent ?? 0) >= 100;
}

/** Progress bar percentage (rounded to 2 decimals); shows 100 when completed or assessment unlocked. */
export function getProgressDisplayPercent(
  progressPercent?: number,
  isCompleted?: boolean,
  lpAssessmentUnlocked?: boolean
): number {
  if (isCompleted || lpAssessmentUnlocked) return 100;
  const clamped = Math.max(0, Math.min(100, progressPercent ?? 0));
  return Math.round(clamped * 100) / 100;
}

/** Show certificate/promo UI unless CMS `awards_certifications` is explicitly `none` (undefined/legacy → still show). */
export function learningPathShowsCertificatePromo(awards_certifications: unknown): boolean {
  return awards_certifications !== 'none';
}

/** Card/profile label for awards; omit when CMS says `none`. */
export function getLearningPathCertificateLabel(awards_certifications: unknown): string | undefined {
  if (awards_certifications === 'none') return undefined;
  if (typeof awards_certifications === 'string' && awards_certifications.length > 0) {
    return `${awards_certifications.charAt(0).toUpperCase()}${awards_certifications.slice(1)}`;
  }
  return 'Certificate';
}
